import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { MediaCollection } from "api/media-library";
import { cn } from "@/lib/utils";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";

interface Props {
  collections: Array<MediaCollection>;
  selectedIds: string[];
  className?: string;
  disabled?: boolean;
  onChange(value: string[]): void;
}

export function CollectionSelectorMulti(props: Props) {
  const [open, setOpen] = useState(false);

  const handleToggle = (collectionId: string) => {
    const isSelected = props.selectedIds.includes(collectionId);
    if (isSelected) {
      props.onChange(props.selectedIds.filter((id) => id !== collectionId));
    } else {
      props.onChange([...props.selectedIds, collectionId]);
    }
  };

  const handleRemove = (collectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    props.onChange(props.selectedIds.filter((id) => id !== collectionId));
  };

  const selectedCollections = props.collections.filter((c) =>
    props.selectedIds.includes(c._id)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full min-h-[38px] h-auto justify-between",
            dropdownTriggerClassName,
            props.className
          )}
          disabled={props.disabled}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedCollections.length > 0 ? (
              selectedCollections.map((collection) => (
                <span
                  key={collection._id}
                  className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded text-sm"
                >
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{
                      background: collection.preferences?.color || "#ccc",
                    }}
                  />
                  <span className="truncate max-w-[120px]">
                    {collection.name}
                  </span>
                  <i
                    className="fa-solid fa-xmark h-3 w-3 text-neutral-400 hover:text-neutral-600 cursor-pointer"
                    onClick={(e) => handleRemove(collection._id, e)}
                  />
                </span>
              ))
            ) : (
              <span className="text-neutral-500">Choose collections...</span>
            )}
          </div>
          <i
            className={cn(
              "fa-solid fa-chevron-down ml-2 h-4 w-4 shrink-0",
              dropdownChevronClassName
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" disableAnimation>
        <Command>
          <CommandInput
            placeholder="Search collections..."
            className="border-neutral-200 h-9"
          />
          <CommandList>
            <CommandEmpty>No collections found.</CommandEmpty>
            <CommandGroup>
              {props.collections.map((collection) => {
                const isSelected = props.selectedIds.includes(collection._id);
                return (
                  <CommandItem
                    key={collection._id}
                    value={collection.name}
                    onSelect={() => handleToggle(collection._id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <div
                        className="h-2 w-2 rounded-full shrink-0"
                        style={{
                          background: collection.preferences?.color || "#ccc",
                        }}
                      />
                      <span className="truncate">{collection.name}</span>
                    </div>
                    <i
                      className={cn(
                        "fa-solid fa-check ml-auto h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
